import type { RouteRecordRaw } from 'vue-router';

/**
 * Routes authentifiees (gameplay). Toutes englobees dans AppLayout via
 * meta.layout='game'. Le guard global redirige vers /login si pas connecte.
 */
export const gameRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },

  // Lot 34C — Lobby de selection d'univers (post-login). Si le joueur
  // n'a aucune partie active, le guard router/index.ts l'amene ici plutot
  // qu'a la home.
  {
    path: '/lobby',
    name: 'lobby',
    component: () => import('@/views/game/UniverseLobbyView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },

  // Lot F2 -- Game core
  {
    path: '/planet',
    name: 'planet-index',
    component: () => import('@/views/game/PlanetIndexView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // Résonance — progression post-cap (niveau 50) : points alloués par piste.
  {
    path: '/resonance',
    name: 'resonance',
    component: () => import('@/views/game/ResonanceView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    // Lot 35B — vue principale de jeu, nouvelle planet home avec
    // top bar ressources + radar env vs ideals + grid de batiments
    // groupes par categorie. L'ancien PlanetView reste accessible
    // sur /planet/:id/legacy pendant la transition (Lot 35C completera
    // les ecrans de gestion par batiment).
    path: '/planet/:id(\\d+)',
    name: 'planet',
    component: () => import('@/views/game/PlanetHomeView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/planet/:id(\\d+)/legacy',
    name: 'planet-legacy',
    component: () => import('@/views/game/PlanetView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // P24 -- Navigation spatiale unifiee. Un seul canvas Three.js qui
  // zoom de l'univers jusqu'a la planete, avec URL stateful pour
  // bookmark / partage / back-button.
  //   /cosmos                       -> niveau univers
  //   /cosmos/g/:n                  -> niveau galaxie #n
  //   /cosmos/g/:n/s/:m             -> niveau systeme #m de la galaxie #n
  //   /cosmos/g/:n/s/:m/p/:p        -> niveau planete #p (globe 3D)
  {
    path: '/cosmos',
    name: 'cosmos',
    component: () => import('@/views/game/CosmosView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/cosmos/g/:n(\\d+)',
    name: 'cosmos-galaxy',
    component: () => import('@/views/game/CosmosView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/cosmos/g/:n(\\d+)/s/:m(\\d+)',
    name: 'cosmos-system',
    component: () => import('@/views/game/CosmosView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/cosmos/g/:n(\\d+)/s/:m(\\d+)/p/:p(\\d+)',
    name: 'cosmos-planet',
    component: () => import('@/views/game/CosmosView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },

  // Anciennes routes -> redirects vers la vue unifiee Cosmos. Permet de
  // ne pas casser les liens deja partages / bookmarks. A garder au moins
  // tant que tous les call sites ont migre.
  {
    path: '/galaxy',
    redirect: '/cosmos',
  },
  {
    path: '/galaxy/:n(\\d+)',
    redirect: to => `/cosmos/g/${to.params.n}`,
  },
  {
    path: '/galaxy/:n(\\d+)/system/:m(\\d+)',
    redirect: to => `/cosmos/g/${to.params.n}/s/${to.params.m}`,
  },
  {
    path: '/universe/:id(\\d+)/map',
    redirect: '/cosmos',
  },

  // Lot F3 -- Recherches + Flottes + Ship designer
  {
    path: '/research',
    name: 'research',
    component: () => import('@/views/game/ResearchView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // Lot 51 - Vue lecture-seule de l'arbre GLOBAL du joueur-univers (wiki §24.7-bis).
  {
    path: '/research/global',
    name: 'research-global',
    component: () => import('@/views/game/ResearchGlobalView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/fleets',
    name: 'fleet-index',
    component: () => import('@/views/game/FleetIndexView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/fleets/:id(\\d+)',
    name: 'fleet',
    component: () => import('@/views/game/FleetView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/ship-designer',
    name: 'ship-designer',
    component: () => import('@/views/game/ShipDesignerView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    // Academie d'officiers (extension wiki §24.6) — chantier recrutement.
    path: '/planets/:planetId(\\d+)/academy',
    name: 'academy',
    component: () => import('@/views/game/AcademyView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },

  // Lot F4 -- Marche + Inbox + News
  {
    path: '/inbox',
    name: 'inbox',
    component: () => import('@/views/game/InboxView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },

  // Lot F6 -- Combat (batailles + rapports)
  {
    path: '/battles',
    name: 'battle-index',
    component: () => import('@/views/game/BattleIndexView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/wrecks',
    name: 'wrecks',
    component: () => import('@/views/game/WrecksView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // Lot 11 — Rapports d'espionnage (wiki §9.3.11)
  {
    path: '/spy-reports',
    name: 'spy-reports',
    component: () => import('@/views/game/SpyReportsView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/stations',
    name: 'stations',
    component: () => import('@/views/game/StationsView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/atelier',
    name: 'station-atelier',
    component: () => import('@/views/game/StationAtelierView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/creatures',
    name: 'creatures',
    component: () => import('@/views/game/CreaturesView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/glyphs',
    name: 'glyphs',
    component: () => import('@/views/game/GlyphsView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // Lot 45A — Vue quetes joueur (§33.13). Backend: PlayerQuestController.
  {
    path: '/quests',
    name: 'quests',
    component: () => import('@/views/game/QuestsView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // Accomplissements (achievements) -- jalons passifs. Backend: AchievementController.
  {
    path: '/achievements',
    name: 'achievements',
    component: () => import('@/views/game/AchievementsView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // Alliance V1 -- creer / inviter / quitter / promouvoir (§24.14.7).
  // Backend : AllianceActionsController (/api/v1/me/alliance).
  {
    path: '/alliance',
    name: 'alliance',
    component: () => import('@/views/game/AllianceView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // Forum communautaire (niveau compte). Backend : ForumController.
  // PUBLIC en lecture (consultable sans compte) : meta.public, pas requiresAuth.
  // Connecté -> layout de jeu (sidebar) ; anonyme -> coquille publique légère
  // (cf App.vue). L'écriture est gatée à l'auth dans les vues + le backend.
  {
    path: '/forum',
    name: 'forum',
    component: () => import('@/views/game/ForumView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/forum/category/:id',
    name: 'forum-category',
    component: () => import('@/views/game/ForumCategoryView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/forum/topic/:id',
    name: 'forum-topic',
    component: () => import('@/views/game/ForumTopicView.vue'),
    meta: { public: true, layout: 'game' },
  },
  // Bibliothèque (encyclopédie) — PUBLIQUE en lecture (comme le forum).
  // Backend : LibraryController. Domaines ajoutés par phases.
  {
    path: '/library',
    name: 'library',
    component: () => import('@/views/game/library/LibraryHubView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/buildings',
    name: 'library-buildings',
    component: () => import('@/views/game/library/LibraryBuildingsView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/research',
    name: 'library-research',
    component: () => import('@/views/game/library/LibraryResearchView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/components',
    name: 'library-components',
    component: () => import('@/views/game/library/LibraryComponentsView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/items',
    name: 'library-items',
    component: () => import('@/views/game/library/LibraryItemsView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/ships',
    name: 'library-ships',
    component: () => import('@/views/game/library/LibraryShipsView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/creatures',
    name: 'library-creatures',
    component: () => import('@/views/game/library/LibraryCreaturesView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/universe',
    name: 'library-universe',
    component: () => import('@/views/game/library/LibraryUniverseView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/anomalies',
    name: 'library-anomalies',
    component: () => import('@/views/game/library/LibraryAnomaliesView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/factions',
    name: 'library-factions',
    component: () => import('@/views/game/library/LibraryFactionsView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/achievements',
    name: 'library-achievements',
    component: () => import('@/views/game/library/LibraryAchievementsView.vue'),
    meta: { public: true, layout: 'game' },
  },
  {
    path: '/library/help/:page?',
    name: 'library-help',
    component: () => import('@/views/game/library/LibraryHelpView.vue'),
    meta: { public: true, layout: 'game' },
  },
  // Lot 50B — Vue blueprints de vaisseaux (§24.12.4). Mockup 06.
  {
    path: '/blueprints',
    name: 'blueprints',
    component: () => import('@/views/game/BlueprintsView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // Items / inventaire joueur (cf wiki §24.12). Liste des items possedes
  // groupes par kind (Artifact/Relic/Document/Cle/...). GET /api/v1/me/items.
  {
    path: '/inventory',
    name: 'inventory',
    component: () => import('@/views/game/InventoryView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // Musee planetaire (cf wiki §24.12.2). Expose Artifact/Relic pour
  // appliquer leurs bonus rolles. Route per planet : /museum/{planetId}.
  {
    path: '/museum/:planetId(\\d+)',
    name: 'museum',
    component: () => import('@/views/game/MuseumView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/chat',
    name: 'chat',
    component: () => import('@/views/game/ChatView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/battles/:id(\\d+)',
    name: 'battle',
    component: () => import('@/views/game/BattleReportView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/battles/:id(\\d+)/replay',
    name: 'battle-replay',
    component: () => import('@/views/game/BattleReplayView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/market',
    name: 'market',
    component: () => import('@/views/game/MarketView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/news',
    name: 'news',
    component: () => import('@/views/game/NewsView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },

  // Lot F5 -- Stats + Leaderboard + Admin
  {
    path: '/leaderboard',
    name: 'leaderboard',
    component: () => import('@/views/game/LeaderboardView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  {
    path: '/me/stats',
    name: 'my-stats',
    component: () => import('@/views/game/MyStatsView.vue'),
    meta: { requiresAuth: true, layout: 'game' },
  },
  // Lot F22 -- Back-office admin (shell + sous-routes par categorie)
  // meta.layout='admin' => rendu en pleine page sans le AppLayout joueur.
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminLayout.vue'),
    meta: { requiresAuth: true, layout: 'admin' },
    children: [
      {
        path: '',
        name: 'admin',
        component: () => import('@/views/admin/AdminDashboardView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      {
        path: 'system/operations',
        name: 'admin-operations',
        component: () => import('@/views/admin/AdminOperationsView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      // CRUD generique pour les entites (mappees dans config/adminEntities.ts).
      {
        path: ':category/:entity',
        name: 'admin-entity',
        component: () => import('@/views/admin/AdminEntityView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      {
        path: 'system/audit-log',
        name: 'admin-audit-log',
        // Le dashboard contient deja l'audit log -- redirige.
        redirect: '/admin',
      },
      {
        path: 'system/factions',
        name: 'admin-factions',
        component: () => import('@/views/admin/AdminFactionsView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      {
        path: 'system/faction-behavior',
        name: 'admin-faction-behavior',
        component: () => import('@/views/admin/AdminFactionBehaviorView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      {
        path: 'system/comptoir',
        name: 'admin-comptoir',
        component: () => import('@/views/admin/AdminComptoirView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      {
        path: 'system/quest-builder',
        name: 'admin-quest-builder',
        component: () => import('@/views/admin/AdminQuestBuilderView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      {
        path: 'system/quest-editor',
        name: 'admin-quest-editor',
        component: () => import('@/views/admin/AdminQuestEditorView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      {
        path: 'system/character-editor',
        name: 'admin-character-editor',
        component: () => import('@/views/admin/CharacterEditorView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      {
        path: 'system/research-tree',
        name: 'admin-research-tree',
        component: () => import('@/views/admin/AdminResearchTreeEditorView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      // Editeur visuel 3D des planetes (preview live des 3 spheres
      // surface + atmosphere + nuages + override meteo, cf wiki §33.x).
      {
        path: 'system/planet-preview',
        name: 'admin-planet-preview',
        component: () => import('@/views/admin/PlanetPreviewView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      // Studio de génération unifié : objets procéduraux + composants de
      // vaisseau (image + génération/plages + simulateur de roll).
      {
        path: 'system/item-studio',
        name: 'admin-item-studio',
        component: () => import('@/views/admin/GenerationStudioView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      // Studio 3D : visualisation des objets 3D du jeu (stations, épaves).
      {
        path: 'system/object-3d-studio',
        name: 'admin-object-3d-studio',
        component: () => import('@/views/admin/Object3DStudioView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      // Éditeur matriciel des courbes de roll (attribut × rareté).
      {
        path: 'generated/roll-curve-matrix',
        name: 'admin-roll-curve-matrix',
        component: () => import('@/views/admin/AttributeRarityMatrixView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
      // Simulateur de combat (flotte vs flotte, Monte-Carlo). Hors-DB.
      {
        path: 'combat/simulator',
        name: 'admin-combat-simulator',
        component: () => import('@/views/admin/AdminCombatSimulatorView.vue'),
        meta: { requiresAuth: true, layout: 'admin' },
      },
    ],
  },
];
